import { client } from '@/lib/sanity'
import type { StockItem } from '@/lib/queries/stockItems'

/**
 * Stock Item service - business logic for stock items
 * All operations use Sanity.io (no local storage)
 */
export const stockItemService = {
  /**
   * Update an existing stock item
   */
  async updateStockItem(
    stockItemId: string,
    updates: Partial<Omit<StockItem, '_id' | '_type' | '_createdAt' | '_updatedAt'>>
  ): Promise<StockItem> {
    console.log('stockItemService.updateStockItem - Updating:', {
      stockItemId,
      updates,
      packageDetails: updates.packageDetails,
    })
    
    // Build the patch operation
    const patch = client.patch(stockItemId)
    
    // Prepare all updates in a single object to avoid multiple set() calls interfering
    const allUpdates: any = {
      _updatedAt: new Date().toISOString(),
    }
    
    // Handle packageDetails separately - build complete object
    if (updates.packageDetails !== undefined) {
      const packageDetails = updates.packageDetails
      
      // Ensure units are always strings (never null/undefined)
      const dimensionUnit = (packageDetails.dimensionUnit && typeof packageDetails.dimensionUnit === 'string') 
        ? packageDetails.dimensionUnit 
        : 'mm'
      const weightUnit = (packageDetails.weightUnit && typeof packageDetails.weightUnit === 'string') 
        ? packageDetails.weightUnit 
        : 'g'
      
      // Build complete packageDetails object with ALL fields explicitly set
      // This ensures the entire nested object is replaced, not merged
      allUpdates.packageDetails = {
        length: packageDetails.length !== undefined ? (packageDetails.length ?? null) : null,
        width: packageDetails.width !== undefined ? (packageDetails.width ?? null) : null,
        height: packageDetails.height !== undefined ? (packageDetails.height ?? null) : null,
        weight: packageDetails.weight !== undefined ? (packageDetails.weight ?? null) : null,
        dimensionUnit: dimensionUnit, // Always set explicitly
        weightUnit: weightUnit, // Always set explicitly
      }
      
      console.log('stockItemService - Setting complete packageDetails object:', allUpdates.packageDetails)
    }
    
    // Add all other fields (excluding packageDetails which we already handled)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { packageDetails, category, ...otherUpdates } = updates
    
    // Add remaining fields first
    Object.assign(allUpdates, otherUpdates)
    
    // Handle category reference explicitly - set it separately to ensure it's saved
    // Category should always be included (either as a reference object or null)
    if (category !== undefined) {
      if (category && typeof category === 'object' && '_ref' in category) {
        // It's a reference object - ensure it has the correct structure
        // Remove 'drafts.' prefix if present to use published document ID
        let categoryId = category._ref
        if (categoryId.startsWith('drafts.')) {
          categoryId = categoryId.replace('drafts.', '')
          console.log('stockItemService - Converting draft category ID to published:', {
            original: category._ref,
            converted: categoryId,
          })
        }
        
        const categoryRef = {
          _type: 'reference',
          _ref: categoryId,
        }
        allUpdates.category = categoryRef
        console.log('stockItemService - Setting category reference:', categoryRef)
      } else {
        // Set to null to clear the category
        allUpdates.category = null
        console.log('stockItemService - Clearing category (setting to null)')
      }
    }
    
    console.log('stockItemService - All updates to apply:', {
      ...allUpdates,
      packageDetails: allUpdates.packageDetails ? '...' : undefined,
      category: allUpdates.category,
      categoryType: typeof allUpdates.category,
      categoryIsObject: allUpdates.category && typeof allUpdates.category === 'object',
      categoryRef: allUpdates.category?._ref,
    })
    
    // Log the exact structure being sent to Sanity (excluding packageDetails for readability)
    const logUpdates = { ...allUpdates }
    if (logUpdates.packageDetails) {
      logUpdates.packageDetails = '[packageDetails object]'
    }
    console.log('stockItemService - Final allUpdates structure:', JSON.stringify(logUpdates, null, 2))
    
    // Set everything in a single set() call to ensure consistency
    const updated = await patch
      .set(allUpdates)
      .commit()

    console.log('stockItemService.updateStockItem - Update result:', {
      _id: updated._id,
      _rev: updated._rev,
      category: updated.category,
      categoryType: typeof updated.category,
      allFields: Object.keys(updated),
    })
    
    return updated as unknown as StockItem
  },

  /**
   * Create a new stock item
   */
  async createStockItem(
    stockItemData: Omit<StockItem, '_id' | '_type' | '_createdAt' | '_updatedAt'>
  ): Promise<StockItem> {
    const stockItem = await client.create({
      _type: 'stockItem',
      ...stockItemData,
      _createdAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
    })

    return stockItem as StockItem
  },

  /**
   * Delete a stock item
   */
  async deleteStockItem(stockItemId: string): Promise<void> {
    await client.delete(stockItemId)
  },
}
